import { Fragment, Node, Schema } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';

import { ParagraphGroups } from './types';

/**
 * 選択範囲の境界に基づいてblockquote内の段落を分類する
 */
export function categorizeParagraphs(
  blockquoteNode: Node,
  blockquoteStart: number,
  selectionFrom: number,
  selectionTo: number,
): ParagraphGroups {
  const selectionStart = Math.max(selectionFrom, blockquoteStart + 1);
  const selectionEnd = Math.min(selectionTo, blockquoteStart + blockquoteNode.nodeSize - 1);

  let currentPosition = blockquoteStart + 1;
  const paragraphGroups: ParagraphGroups = {
    before: [],
    selected: [],
    after: [],
  };

  blockquoteNode.forEach((child: Node) => {
    const childStart = currentPosition;
    const childEnd = currentPosition + child.nodeSize;

    if (childEnd <= selectionStart) {
      paragraphGroups.before.push(child);
    } else if (childStart >= selectionEnd) {
      paragraphGroups.after.push(child);
    } else {
      paragraphGroups.selected.push(child);
    }

    currentPosition = childEnd;
  });

  return paragraphGroups;
}

/**
 * 部分的に選択されたblockquoteの置換ノードを作成する
 */
export function createReplacementNodes(
  schema: Schema,
  originalNode: Node,
  paragraphGroups: ParagraphGroups,
): Node[] {
  const replacementNodes: Node[] = [];

  // 選択前の段落用のblockquoteを作成
  if (paragraphGroups.before.length > 0) {
    const beforeBlockquote = schema.nodes.blockquote.create(
      originalNode.attrs,
      Fragment.from(paragraphGroups.before),
    );
    replacementNodes.push(beforeBlockquote);
  }

  // 選択された段落を通常の段落として追加（引用解除）
  replacementNodes.push(...paragraphGroups.selected);

  // 選択後の段落用のblockquoteを作成
  if (paragraphGroups.after.length > 0) {
    const afterBlockquote = schema.nodes.blockquote.create(
      originalNode.attrs,
      Fragment.from(paragraphGroups.after),
    );
    replacementNodes.push(afterBlockquote);
  }

  return replacementNodes;
}

/**
 * 選択範囲内の段落を取得する
 */
export function getSelectedParagraphs(state: EditorState, from: number, to: number): Node[] {
  const { schema } = state;
  const selectedParagraphs: Node[] = [];
  const $from = state.doc.resolve(from);
  const $to = state.doc.resolve(to);

  const startBlockPos = $from.before($from.depth);
  const endBlockPos = $to.after($to.depth);

  state.doc.nodesBetween(startBlockPos, endBlockPos, (node, pos) => {
    if (node.type === schema.nodes.paragraph && pos >= startBlockPos && pos < endBlockPos) {
      selectedParagraphs.push(node);
    }
  });

  return selectedParagraphs;
}

/**
 * blockquoteから段落を抽出する
 */
export function extractParagraphsFromBlockquote(blockquoteNode: Node, schema: Schema): Node[] {
  const paragraphs: Node[] = [];
  blockquoteNode.forEach((child) => {
    if (child.type === schema.nodes.paragraph) {
      paragraphs.push(child);
    }
  });
  return paragraphs;
}
